import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, Waves, Moon, Flame, Sparkles, Heart, TrendingUp, User as UserIcon,
  Settings as SettingsIcon, Bell, Shield, LogOut, ChevronRight, Video, Wand2, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * MorePage — the fifth-tab hub. Bundles every non-primary destination
 * (Programs, Sleep, Soundscapes, Journal, Manifestations, Favorites,
 * Progress, Profile, Settings) into a single, scannable directory. The
 * original ProfilePage still exists at /profile and stays fully functional —
 * this page is a spacious index that surfaces it alongside everything else.
 */

// Groups render as titled sections. Each item is a link + short blurb.
const GROUPS = [
  {
    title: 'Journeys',
    items: [
      { to: '/programs', icon: Flame, label: 'Programs', hint: 'Multi-day guided series with Rowdy', accent: '#D6AD60' },
      { to: '/manifest', icon: Sparkles, label: 'Manifestations', hint: '300+ affirmations across 10 themes', accent: '#B8934D' },
    ],
  },
  {
    title: 'Rest & Recharge',
    items: [
      { to: '/category/sleep', icon: Moon, label: 'Sleep', hint: 'Sleep meditations & bedtime stories', accent: '#1e3a5f' },
      { to: '/sounds', icon: Waves, label: 'Soundscapes', hint: 'Rain, fireplace, nature, lofi', accent: '#4a7c65' },
      { to: '/videos', icon: Video, label: 'Videos', hint: 'Long-form reflections with Rowdy', accent: '#6b8e6a' },
    ],
  },
  {
    title: 'Your practice',
    items: [
      { to: '/journal', icon: BookOpen, label: 'Journal', hint: 'Private entries + gentle prompts', accent: '#8a7e60' },
      { to: '/profile#saved', icon: Heart, label: 'Favorites', hint: 'Reflections you saved for later', accent: '#D6AD60' },
      { to: '/profile#progress', icon: TrendingUp, label: 'Progress', hint: 'Minutes, sessions, showing up', accent: '#5B7A65' },
    ],
  },
  {
    title: 'Account',
    items: [
      { to: '/profile', icon: UserIcon, label: 'Profile', hint: 'Name, photo, friends', accent: '#7a9b8e' },
      { to: '/personalize', icon: Wand2, label: 'Personalize my experience', hint: 'What you want support with', accent: '#C79A4A' },
      { to: '/membership', icon: ShieldCheck, label: 'Membership', hint: 'Reflections Free · Explore Premium', accent: '#173F35' },
      { to: '/profile#notifications', icon: Bell, label: 'Notifications', hint: 'Reminders + push preferences', accent: '#a8bfae' },
      { to: '/profile#settings', icon: SettingsIcon, label: 'Settings', hint: 'App preferences', accent: '#8a7e60' },
    ],
  },
];

const MoreItem = ({ to, icon: Icon, label, hint, accent, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="flex items-center gap-3 p-3.5 rounded-2xl bg-[#FFFCF3] border border-stone-200/70 hover:border-[#D6AD60]/40 hover:shadow-sm transition-all active:scale-[0.99]"
  >
    <div
      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{
        background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
        color: '#FFFCF3',
      }}
    >
      <Icon className="w-5 h-5" />
    </div>
    <div className="flex-1 min-w-0">
      <p
        className="text-[15px] font-semibold text-stone-800 leading-tight"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        {label}
      </p>
      <p className="text-[12px] text-stone-500 mt-0.5 truncate">{hint}</p>
    </div>
    <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
  </Link>
);

const MorePage = () => {
  const { user, streak, logout } = useAuth();

  return (
    <div className="pb-32 min-h-screen bg-[#F5EFE0]" data-testid="more-page">
      {/* Hero */}
      <div className="px-5 pt-6 pb-4">
        <p className="rbr-eyebrow mb-2" style={{ color: '#D6AD60' }}>More</p>
        <h1
          className="rbr-heading text-3xl leading-tight text-stone-800 mb-1"
        >
          {user ? `Hi, ${user.name?.split(' ')[0] || 'friend'}` : 'Welcome'}
        </h1>
        <p className="text-sm text-stone-500">
          Everything else you might need — one tap away.
        </p>
      </div>

      {/* Streak stat card — only if a user is logged in and has a streak */}
      {user && (
        <div className="px-5 mb-5">
          <Link
            to="/profile#progress"
            data-testid="more-streak-card"
            className="flex items-center justify-between gap-4 p-4 rounded-2xl transition-all active:scale-[0.99] hover:shadow-md"
            style={{
              background: 'linear-gradient(135deg, #173F35 0%, #245549 100%)',
              boxShadow: '0 6px 18px rgba(23,63,53,0.20)',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center"
                style={{ background: 'rgba(214,173,96,0.22)' }}
              >
                <Flame className="w-6 h-6" style={{ color: '#D6AD60' }} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: '#D6AD60' }}>
                  You've shown up
                </p>
                <p className="text-lg font-semibold" style={{ color: '#F8F6F0', fontFamily: "'Playfair Display', serif" }}>
                  {streak.current} {streak.current === 1 ? 'day' : 'days'}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5" style={{ color: 'rgba(248,246,240,0.6)' }} />
          </Link>
        </div>
      )}

      {/* Groups */}
      <div className="px-5 space-y-5">
        {GROUPS.map((g) => (
          <section key={g.title}>
            <p className="rbr-eyebrow mb-2.5 text-stone-500">{g.title}</p>
            <div className="space-y-2">
              {g.items.map((it) => (
                <MoreItem
                  key={it.to}
                  {...it}
                  testid={`more-item-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                />
              ))}
            </div>
          </section>
        ))}

        {/* Admin quick-links (only shown to admins) */}
        {user?.isAdmin && (
          <section>
            <p className="rbr-eyebrow mb-2.5 text-stone-500">Admin</p>
            <div className="space-y-2">
              <MoreItem
                to="/admin/content"
                icon={SettingsIcon}
                label="Content Manager"
                hint="Create, edit, and import reflections"
                accent="#173F35"
                testid="more-item-admin-content"
              />
              <MoreItem
                to="/moderation"
                icon={Shield}
                label="Moderation Queue"
                hint="Review reported comments"
                accent="#8a5a3d"
                testid="more-item-moderation"
              />
            </div>
          </section>
        )}

        {/* Sign out */}
        {user && (
          <div className="pt-4">
            <button
              type="button"
              onClick={logout}
              data-testid="more-logout-btn"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-white/70 border border-stone-200 text-stone-600 text-sm font-medium hover:bg-white hover:text-stone-800 transition-all active:scale-[0.99]"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        )}

        {/* If not logged in, invite to sign in */}
        {!user && (
          <div className="pt-2">
            <Link
              to="/auth"
              data-testid="more-signin-cta"
              className="block w-full text-center py-3 rounded-2xl bg-[#D6AD60] text-[#1a1814] font-semibold text-sm active:scale-[0.99] transition-transform"
            >
              Sign in to unlock everything
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MorePage;
